import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import useAppContext from './useAppContext';

const BodyScrollBlockContext = createContext({
  isBlockedBy: [],
  setIsBlockedBy: () => null,
});

const BodyScrollBlockProvider = ({ children }) => {
  const { scrollBarWidth } = useAppContext();
  const [isBlockedBy, setIsBlockedBy] = useState([]);

  const blocksBodyScroll = useCallback(({ identifier, state }) => {
    setIsBlockedBy(prevState => {
      if (state) {
        const isBlockedBySet = new Set(prevState);
        isBlockedBySet.add(identifier);

        return Array.from(isBlockedBySet);
      }

      return prevState.filter(id => id !== identifier);
    });
  }, []);

  const isBlocked = useMemo(() => isBlockedBy.length > 0, [isBlockedBy]);

  const forceUnblockBodyScroll = useCallback(() => {
    setIsBlockedBy([]);
  }, [setIsBlockedBy]);

  const value = useMemo(
    () => ({ isBlocked, blocksBodyScroll, forceUnblockBodyScroll }),
    [isBlocked, blocksBodyScroll, forceUnblockBodyScroll],
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => null;
    }

    document.body.style.overflow = isBlocked ? 'hidden' : '';
    document.body.style.paddingRight = isBlocked ? `${scrollBarWidth}px` : '';

    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isBlocked, scrollBarWidth]);

  return <BodyScrollBlockContext.Provider value={value}>{children}</BodyScrollBlockContext.Provider>;
};

const useBodyScrollBlock = () => {
  const id = useRef(uuidv4());
  const { isBlocked, blocksBodyScroll, forceUnblockBodyScroll } = useContext(BodyScrollBlockContext);

  const changeBodyScrollState = useCallback(
    state => {
      blocksBodyScroll({ identifier: id.current, state });
    },
    [blocksBodyScroll, id],
  );

  return { identifier: id.current, blocksBodyScroll: changeBodyScrollState, isBlocked, forceUnblockBodyScroll };
};

export { BodyScrollBlockProvider };
export default useBodyScrollBlock;
